/**
 * englishAssessment.js — client for the My World Quest English
 * Assessment (CEFR). Talks to /api/english-assessment, feeds the
 * existing masteryEngine with assessment evidence, stores an
 * assessment history locally (no account) for longitudinal growth,
 * and maps CEFR results → personalised Language Quest recommendations.
 */
import { recordAttempt } from "@/lib/masteryEngine";
import { track } from "@/lib/track";
import { getActiveChildId } from "@/lib/children";

const API = process.env.REACT_APP_BACKEND_URL;
const EA = `${API}/api/english-assessment`;
const HISTORY_KEY = "mwq_english_assessments_v1";
const LATEST_KEY = "mwq_english_latest_result_v1";
const INPROGRESS_KEY = "mwq_english_session_v1";
const BEST_KEY = "mwq_english_best_levels_v1";
const REASSESS_WEEKS = 8;

// Block 4 — scope all local assessment data to the ACTIVE child profile so
// siblings never share results. Legacy unscoped data migrates in once.
function activeId() { try { return getActiveChildId() || "default"; } catch { return "default"; } }
function scoped(base) { return `${base}::${activeId()}`; }
function readScoped(base, fallback) {
  try {
    const sk = scoped(base);
    let raw = localStorage.getItem(sk);
    if (raw == null) {
      const legacy = localStorage.getItem(base);   // one-time migration into scope
      if (legacy != null) { localStorage.setItem(sk, legacy); raw = legacy; }
    }
    return raw == null ? fallback : JSON.parse(raw);
  } catch { return fallback; }
}

async function j(path, opts = {}) {
  const res = await fetch(`${EA}${path}`, {
    method: opts.method || "GET",
    headers: { "Content-Type": "application/json" },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Something went wrong");
  return data;
}

export const getLevels = () => j("/levels");
export const getBankStats = () => j("/bank-stats");
export const startSession = (age, skills) => j("/session/start", { method: "POST", body: { age, skills } });
export const answerItem = (session_id, item_id, response) => j("/session/answer", { method: "POST", body: { session_id, item_id, response } });
export const getWritingPrompt = (session_id) => j(`/writing/prompt?session_id=${encodeURIComponent(session_id)}`);
export const scoreWriting = (session_id, text) => j("/writing/score", { method: "POST", body: { session_id, text } });
export const getResult = (session_id) => j(`/session/${encodeURIComponent(session_id)}/result`);
export const getCurrent = (session_id) => j(`/session/${encodeURIComponent(session_id)}/current`);
export const shareToClass = (session_id, class_code, nickname) => j("/public/share", { method: "POST", body: { session_id, class_code, nickname } });
export const skipListening = (session_id) => j("/session/skip-listening", { method: "POST", body: { session_id } });

// Resumable session (no account): remember the in-progress session id (per profile).
export function saveInProgress(id) { try { localStorage.setItem(scoped(INPROGRESS_KEY), id); } catch { /* ignore */ } }
export function loadInProgress() { try { return localStorage.getItem(scoped(INPROGRESS_KEY)) || localStorage.getItem(INPROGRESS_KEY) || ""; } catch { return ""; } }
export function clearInProgress() { try { localStorage.removeItem(scoped(INPROGRESS_KEY)); localStorage.removeItem(INPROGRESS_KEY); } catch { /* ignore */ } }

// ── Real activity → skill map (Block 6) ───────────────────────────────
// Each activity declares which skill(s) it GENUINELY develops and the CEFR
// band range it suits. "Fill in the blank" / "Pick the meaning" are reading
// & vocabulary — they are NOT listening activities.
const LEVEL_ORDER = ["pre-a1", "a1", "a2", "b1", "b1+"];
const levelIdx = (id) => Math.max(0, LEVEL_ORDER.indexOf(id));

const ACTIVITY_CATALOG = [
  { id: "story-explorer", label: "Story Explorer", tagline: "Read a short story & answer", path: "/language-quest", skills: ["reading", "listening"], min: "a1", max: "b1+" },
  { id: "meaning-match", label: "Meaning Match", tagline: "Match words to meanings", path: "/language-quest", skills: ["reading"], min: "pre-a1", max: "b1+" },
  { id: "missing-letter", label: "Missing Letter", tagline: "Finish the spelling", path: "/language-quest", skills: ["reading"], min: "pre-a1", max: "a2" },
  { id: "word-builder", label: "Word Builder", tagline: "Build the word from letters", path: "/language-quest", skills: ["reading"], min: "pre-a1", max: "a2" },
  { id: "sentence-snap", label: "Sentence Snap", tagline: "Put the sentence in order", path: "/language-quest", skills: ["reading", "writing"], min: "a1", max: "b1+" },
  { id: "word-chain", label: "Word Chain", tagline: "Link words that go together", path: "/language-quest", skills: ["reading"], min: "a2", max: "b1+" },
  { id: "greetings-quest", label: "Greetings Quest", tagline: "Listen & choose the reply", path: "/language-quest", skills: ["listening"], min: "pre-a1", max: "a2" },
  { id: "write-share", label: "Write & Share", tagline: "Write a little more, your way", path: "/english-assessment", skills: ["writing"], min: "pre-a1", max: "b1+" },
];

const ROLE_LABEL = { consolidate: "Play to your strength", stretch: "Stretch a little", support: "A gentle boost" };

function pickActivity(skill, targetIdx, used) {
  const inBand = ACTIVITY_CATALOG.filter((a) => a.skills.includes(skill) && !used.has(a.id)
    && levelIdx(a.min) <= targetIdx && targetIdx <= levelIdx(a.max));
  const pool = inBand.length ? inBand : ACTIVITY_CATALOG.filter((a) => a.skills.includes(skill) && !used.has(a.id));
  return pool[0] || null;
}

/** Block 6: select 3 genuinely-different quests from the activity→skill map,
 *  filtered by the child's actual profile. One consolidation at their
 *  STRONGEST skill, one growth-edge at the MIDDLE, one supported at the
 *  WEAKEST — so a weak-reading and a weak-listening result differ. If fewer
 *  than 3 genuinely match, return fewer and say so (6d). */
export function recommendQuests(result) {
  const skills = result?.skills || {};
  const assessed = Object.entries(skills).filter(([, v]) =>
    (v.status ? v.status === "ASSESSED" : !!v.estimatedLevel) && v.estimatedLevel);
  if (!assessed.length) return { picks: [], note: null };
  const ranked = [...assessed].sort((a, b) => levelIdx(b[1].estimatedLevel) - levelIdx(a[1].estimatedLevel)); // strongest → weakest
  const strongest = ranked[0];
  const weakest = ranked[ranked.length - 1];
  const middle = ranked.length >= 3 ? ranked[1] : null;
  const used = new Set();
  const picks = [];
  const add = (entry, role, targetIdx) => {
    if (!entry) return;
    const [skill, v] = entry;
    const a = pickActivity(skill, targetIdx, used);
    if (a) { used.add(a.id); picks.push({ ...a, forSkill: skill, atLevel: v.estimatedLevel, role }); }
  };
  add(strongest, "consolidate", levelIdx(strongest[1].estimatedLevel));
  if (middle) add(middle, "stretch", Math.min(levelIdx(middle[1].estimatedLevel) + 1, LEVEL_ORDER.length - 1));
  if (weakest !== strongest) add(weakest, "support", Math.max(levelIdx(weakest[1].estimatedLevel) - 1, 0));
  // Fewer than 3 assessed skills — top up with a distinct stretch for the
  // skills we do have, never padding with mislabelled activities.
  if (picks.length < 3) {
    for (const [skill, v] of ranked) {
      const a = pickActivity(skill, Math.min(levelIdx(v.estimatedLevel) + 1, LEVEL_ORDER.length - 1), used);
      if (a) { used.add(a.id); picks.push({ ...a, forSkill: skill, atLevel: v.estimatedLevel, role: "stretch" }); }
      if (picks.length >= 3) break;
    }
  }
  const note = picks.length < 3
    ? "These are the activities that genuinely match your results right now — we'd rather show a few great ones than pad the list."
    : null;
  return { picks, note, roleLabel: ROLE_LABEL };
}

// ── mastery evidence + local history (growth) ─────────────────────────
/** Feed assessment evidence into the existing masteryEngine and persist
 *  the assessment record locally so a later reassessment can show growth. */
export function commitAssessment(result) {
  const skills = result?.skills || {};
  Object.entries(skills).forEach(([skill, v]) => {
    if (!v.estimatedLevel && v.status !== "ASSESSED") return;
    try {
      // Register English learning activity + a starting point. Higher
      // estimated levels seed more "correct" evidence points.
      const reps = 1 + levelIdx(v.estimatedLevel);
      for (let i = 0; i < reps; i++) {
        recordAttempt(`english-${skill}`, { correct: true, strand: "reading-writing" });
      }
    } catch { /* ignore */ }
  });
  const history = loadHistory();
  history.push({
    at: Date.now(),
    ageBand: result.ageBand || null,
    assessmentVersion: result.assessmentVersion,
    itemBankVersion: result.itemBankVersion,
    skills: Object.fromEntries(Object.entries(skills).map(([k, v]) => [k, {
      estimatedLevel: v.estimatedLevel || null, confidence: v.confidence || null,
      status: v.status || (v.estimatedLevel ? "ASSESSED" : "NOT_MEASURED"),
    }])),
  });
  try { localStorage.setItem(scoped(HISTORY_KEY), JSON.stringify(history.slice(-10))); } catch { /* ignore */ }
  try { localStorage.setItem(scoped(LATEST_KEY), JSON.stringify(result)); } catch { /* ignore */ }
  updateBestLevels(skills);
  track("assessment_completed", { skills: Object.keys(skills).length });
}

export function loadLatestResult() {
  return readScoped(LATEST_KEY, null);
}

export function loadHistory() {
  const h = readScoped(HISTORY_KEY, []);
  return Array.isArray(h) ? h : [];
}

// Block 4 — a profile's best-ever level per skill (never lowered), so a bad
// day never erases what the child has shown they can do.
export function loadBestLevels() {
  const b = readScoped(BEST_KEY, {});
  return (b && typeof b === "object") ? b : {};
}
function updateBestLevels(skills) {
  const best = loadBestLevels();
  Object.entries(skills).forEach(([s, v]) => {
    const lv = v.estimatedLevel;
    if (lv && (!best[s] || levelIdx(lv) > levelIdx(best[s]))) best[s] = lv;
  });
  try { localStorage.setItem(scoped(BEST_KEY), JSON.stringify(best)); } catch { /* ignore */ }
}

/** Compare the latest two assessments for a growth view.
 *  Block 4: only compare within the SAME age band (never across bands),
 *  and only on skills that were ASSESSED in both. Returns anyUp / anyDrop
 *  so the UI can celebrate growth but never celebrate a regression. */
export function growthDelta() {
  const h = loadHistory();
  if (h.length < 2) return null;
  const now = h[h.length - 1];
  // Find the most recent PRIOR attempt in the same age band.
  const prev = [...h.slice(0, -1)].reverse().find((r) => (r.ageBand || null) === (now.ageBand || null));
  if (!prev) return null;
  const out = {};
  let anyUp = false, anyDrop = false, maxDrop = 0;
  Object.keys(now.skills).forEach((s) => {
    const a = prev.skills[s]?.estimatedLevel;
    const b = now.skills[s]?.estimatedLevel;
    const aOk = prev.skills[s]?.status !== "NOT_MEASURED";
    const bOk = now.skills[s]?.status !== "NOT_MEASURED";
    if (!a || !b || !aOk || !bOk) return;
    const d = levelIdx(b) - levelIdx(a);
    if (d > 0) anyUp = true;
    if (d < 0) { anyDrop = true; maxDrop = Math.max(maxDrop, -d); }
    out[s] = { from: a, to: b, up: d > 0, down: d < 0 };
  });
  if (Object.keys(out).length === 0) return null;
  // Block 4 — baseline preservation: flag a suspicious ≥2-band drop from the
  // child's best-ever (same band) when today's evidence isn't high-confidence,
  // so we reassure rather than record a regression as their new level.
  const priorSameBand = h.slice(0, -1).filter((r) => (r.ageBand || null) === (now.ageBand || null));
  const bestPrior = {};
  priorSameBand.forEach((r) => Object.entries(r.skills).forEach(([s, v]) => {
    if (v.status !== "NOT_MEASURED" && v.estimatedLevel
      && (!bestPrior[s] || levelIdx(v.estimatedLevel) > levelIdx(bestPrior[s]))) bestPrior[s] = v.estimatedLevel;
  }));
  let suspect = false; const preserved = {};
  Object.keys(now.skills).forEach((s) => {
    const nowLv = now.skills[s]?.estimatedLevel;
    const nowOk = now.skills[s]?.status !== "NOT_MEASURED";
    const conf = now.skills[s]?.confidence;
    const bp = bestPrior[s];
    if (nowLv && nowOk && bp && (levelIdx(bp) - levelIdx(nowLv)) >= 2 && conf !== "high") {
      suspect = true; preserved[s] = { best: bp, now: nowLv };
    }
  });
  return { prevAt: prev.at, nowAt: now.at, skills: out, anyUp, anyDrop, maxDrop, suspect, preserved };
}

export function daysUntilReassess() {
  const h = loadHistory();
  if (!h.length) return 0;
  const last = h[h.length - 1].at;
  const due = last + REASSESS_WEEKS * 7 * 24 * 3600 * 1000;
  return Math.max(0, Math.ceil((due - Date.now()) / (24 * 3600 * 1000)));
}
